export enum LocalStorageKey {
  IMPERSONATION_COOKIE_CHANGE = "__chalk__.impersonation_cookie_change",
  GLOBAL_APP_STATE = "__chalk__.admin_mode", // should not be named this, can't be renamed for backwards compatibility
  PLAN_OPTIONS = "__chalk__.query_plan_options",
  MARKETING_QUERY_PLAN_OPTIONS = "__chalk__.marketing_query_plan_options_v3",
  SQL_QUERY_PLAN_OPTIONS = "__chalk__.sql_query_plan_options",
  ADMIN_OPTIONS = "__chalk__.admin_options",
  UTM_PARAMS = "__chalk__.utm_params",
  INITIAL_UTM_PARAMS = "__chalk__.utm_params_initial",
  SQL_EXPLORER_RUN_OPTIONS = "__chalk__.sql_explorer_run_options",
  FEATURE_STUDIO_SELECTED_BRANCH = "__chalk__.feature_studio_selected_branch",
  PYLON_CHAT_BUBBLE_HIDDEN = "__chalk__.pylon_chat_bubble_hidden",
  CHART_DISPLAY_OPTIONS = "__chalk__.chart_display_options",
  KUBERNETES_POD_VISIBILITY = "__chalk__.kubernetes_pod_visibility",
  // Visible column ids for the offline queries table. Stored as { visibleColumns: string[] }.
  OFFLINE_QUERIES_VISIBLE_COLUMNS = "__chalk__.offline_queries_visible_columns",
  // Display formats selected for columns in the offline queries table.
  OFFLINE_QUERIES_COLUMN_FORMATS = "__chalk__.offline_queries_column_formats",
  // Visible column ids for the online queries table. Stored as { visibleColumns: string[] }.
  ONLINE_QUERIES_VISIBLE_COLUMNS = "__chalk__.online_queries_visible_columns",
  DASHBOARD_FIXTURE_CAPTURE_ENABLED = "__chalk__.dashboard_fixture_capture_enabled",
  DASHBOARD_FIXTURE_CAPTURE_CRAWL_STATE = "__chalk__.dashboard_fixture_capture_crawl_state",
  DASHBOARD_FIXTURE_CAPTURE_STATE = "__chalk__.dashboard_fixture_capture_state",
  DASHBOARD_FIXTURE_REPLAY_ENABLED = "__chalk__.dashboard_fixture_replay_enabled",
  DASHBOARD_FIXTURE_REPLAY_BUNDLE = "__chalk__.dashboard_fixture_replay_bundle",
  // Live query "watches" in the branch source editor. Stored as Record<`${envId}:${branch}`, Watch[]>.
  BRANCH_QUERY_WATCHES = "__chalk__.branch_query_watches",
  // Saved stream-simulator scenarios in the branch source editor. Stored as
  // Record<`${envId}:${branch}`, SimScenario[]>.
  BRANCH_STREAM_SCENARIOS = "__chalk__.branch_stream_scenarios",
  // Persisted width (px) of the branch source editor sidebar. Stored as { width: number }.
  BRANCH_EDITOR_SIDEBAR_WIDTH = "__chalk__.branch_editor_sidebar_width",
  // Persisted width (px) of the read-only source viewer sidebar. Stored as { width: number }.
  SOURCE_EDITOR_SIDEBAR_WIDTH = "__chalk__.source_editor_sidebar_width",
  // Persisted details-sidebar widths keyed by Next.js route template.
  // Stored as { widthByRoute: Record<string, number> }.
  DETAILS_SIDEBAR_WIDTHS = "__chalk__.details_sidebar_widths",
  // Persisted height (px) of the branch source editor stream-simulator bottom drawer.
  // Stored as { height: number }.
  BRANCH_STREAM_DRAWER_HEIGHT = "__chalk__.branch_stream_drawer_height",
  // Selected mock response latency in the test-components harness. Stored as { latencyId: string }.
  TEST_COMPONENTS_MOCK_LATENCY = "__chalk__.test_components_mock_latency",
  // Selected mock data state in the test-components harness. Stored as { dataStateId: string }.
  TEST_COMPONENTS_DATA_STATE = "__chalk__.test_components_data_state",
  // Latest sandbox terminal session id by `${environmentId}:${sandboxId}`.
  SANDBOX_TERMINAL_SESSIONS = "__chalk__.sandbox_terminal_sessions",
  // Whether the card announcing the assistant has been put away. Stored as
  // { dismissedAt: string | null }.
  ASSISTANT_NUDGE = "__chalk__.assistant_nudge",
  // Whether the admin network log drawer is open, so it survives reloads and
  // capture spans the next page load. Stored as "true" / "false".
  NETWORK_LOG_PANEL_OPEN = "__chalk__.network_log_panel_open",
  // Per-result notebook column display preferences.
  NOTEBOOK_COLUMN_FORMATS = "__chalk__.notebook_column_formats",
  // Opt-in vim keybindings in the notebook editor. Stored as { enabled: boolean }.
  NOTEBOOK_VIM_MODE = "__chalk__.notebook_vim_mode",
  // AI typeahead (inline ghost-text completions) in notebook cells. Stored as
  // { enabled: boolean | null }; null defers to AI-provider availability.
  NOTEBOOK_AI_TYPEAHEAD = "__chalk__.notebook_ai_typeahead",
  // Last-used AI provider, keyed by environment id. Stored as
  // { connectionIdByEnvironment: Record<string, string> }; an empty string
  // means the built-in Chalk provider.
  ASSISTANT_AI_PROVIDER = "__chalk__.assistant_ai_provider",
  // The assistant conversation that was open and focused when the user last
  // left the workspace, keyed by environment id. Stored as
  // { conversationIdByEnvironment: Record<string, string> }; a missing entry
  // means nothing was open, so no chat is restored.
  ASSISTANT_FOCUSED_CONVERSATION = "__chalk__.assistant_focused_conversation",
  // The assistant's tool approval mode per environment, stored as
  // { skipToolApprovalByEnvironment: Record<string, boolean> } so Auto-approve
  // survives remounts and new conversations. A missing entry means Manual.
  ASSISTANT_TOOL_APPROVAL = "__chalk__.assistant_tool_approval",
  // Whether the (hidden, employee-only) LineageService debug panel's drawer is open.
  LINEAGE_PANEL_OPEN = "__chalk__.lineage_panel_open",
  // Detail pages visited most recently, for the project sidebar's Recent section. Stored as
  // { entriesByEnvironment: Record<`${projectId}:${environmentId}`, ProjectNavHistoryEntry[]> }.
  PROJECT_NAV_HISTORY = "__chalk__.project_nav_history",
  // Pages pinned to the project sidebar's Pinned section. Stored as
  // { pinsByEnvironment: Record<`${projectId}:${environmentId}`, ProjectNavPin[]> }.
  PROJECT_NAV_PINS = "__chalk__.project_nav_pins",
  // Sidebar items pinned to the project rail as shortcuts. Nav items are the same in every
  // environment, so this is one list per browser. Stored as { itemIds: string[] }.
  PROJECT_NAV_RAIL_PINS = "__chalk__.project_nav_rail_pins",
  // The reader's order for the project rail's category icons, one per browser like the pins.
  // Stored as { itemIds: string[] }; categories it does not name keep their default places.
  PROJECT_NAV_RAIL_CATEGORY_ORDER = "__chalk__.project_nav_rail_category_order",
  // The reader's order for each category sidebar's sections, one per browser like the rail's.
  // Stored as { byCategory: Record<categoryId, string[]> }; sections it does not name keep their
  // default places.
  PROJECT_NAV_SIDEBAR_SECTION_ORDER = "__chalk__.project_nav_sidebar_section_order",
  // The reader's arrangement of the category sidebars' rows, one per browser like the sections'.
  // Stored as { orderBySection: Record<sectionId, string[]>, sectionByRow: Record<rowId,
  // sectionId> } (SidebarRowLayout); rows a section's order does not name keep their default places.
  PROJECT_NAV_SIDEBAR_ROW_LAYOUT = "__chalk__.project_nav_sidebar_row_layout",
  // A Chalk employee's per-browser choice of navigation, which applies in every environment.
  // Stored as { mode: "flag" | "classic" | "category" }; absent is the default (the category nav
  // for Chalk employees, the flag for everyone else).
  CATEGORY_NAV_OVERRIDE = "__chalk__.category_nav_override",
  // Whether this browser's signed-in reader could arrange the category nav when the session last
  // resolved, which the nav follows while the next session loads. Stored as { arranges: boolean }.
  CATEGORY_NAV_ARRANGES = "__chalk__.category_nav_arranges",
  // The environments whose Overview setup guide this browser dismissed. Stored as
  // { dismissedEnvironmentIds: string[] }.
  SETUP_GUIDE_DISMISSAL = "__chalk__.setup_guide_dismissal",
  // When this browser dismissed the callout on the top rail's navigation switch that introduces
  // the navigation experiment. Stored as { dismissedAt: ISO timestamp }.
  NAV_EXPERIMENT_CALLOUT_DISMISSAL = "__chalk__.nav_experiment_callout_dismissal",
  // Whether the reader last hid or showed the category nav's secondary sidebar, so a reload keeps
  // it that way. Stored as { hidden: boolean }; the classic nav neither reads nor writes it.
  PROJECT_SECONDARY_SIDEBAR_VISIBILITY = "__chalk__.project_secondary_sidebar_visibility",
  // Evaluation task viewer verdicts, notes, and reference-dataset picks, keyed by
  // `<evaluationRunId>/<task key>`. Stored as { [key]: TaskRendererAnnotation }.
  EVALUATION_TASK_ANNOTATIONS = "__chalk__.evaluation_task_annotations",
  // Per-evaluation task renderer URLs that override the evaluation's declared renderer while a
  // developer iterates on one locally. Stored as { [evaluationId]: url }.
  EVALUATION_TASK_RENDERER_OVERRIDES = "__chalk__.evaluation_task_renderer_overrides",
  // Whether this browser dismissed Compute Home's invite-your-team banner. Stored as
  // { dismissed: boolean }.
}

/** Key used with sessionStorage to pass query text from SQL query detail page to the SQL editor. */
export const SQL_EDITOR_PREFILL_KEY = "__chalk__.sql_editor_prefill";
